import { faMicrophone, faPause, faPlay, faStop, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styles } from "@repo/helpers";
import { SerializedFile } from "@repo/models";
import { useWavesurfer } from "@wavesurfer/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HoverPlugin from "wavesurfer.js/dist/plugins/hover.esm.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";

import { Button } from "../button";
import { ButtonSize, ButtonTypes } from "../button/types";
import { InputComponent } from "../input";
import style from "./style.module.scss";
import { Props } from "./types";

export function AudioRecording(props: Props) {
	const { label, value: audioRecording, changeHandler } = props;
	const {
		waveColor = "rgb(200, 0, 200)",
		progressColor = "rgb(100, 0, 100)",
		showHover = true,
		hoverLineColor = "#ff0000",
		hoverLineWidth = 2,
		hoverLabelBackground = "#555",
		hoverLabelColor = "#fff",
		hoverLabelSize = "11px",
	} = props;
	const [isRecording, setIsRecording] = useState(false);
	const isSaving = useRef<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const recordPlugin = RecordPlugin.create({
		scrollingWaveform: true,
		renderRecordedAudio: true,
	});
	const hoverPlugin = HoverPlugin.create({
		lineColor: hoverLineColor,
		lineWidth: hoverLineWidth,
		labelBackground: hoverLabelBackground,
		labelColor: hoverLabelColor,
		labelSize: hoverLabelSize,
	});

	const { wavesurfer, isPlaying } = useWavesurfer({
		normalize: true,
		container: containerRef,
		waveColor: waveColor,
		progressColor: progressColor,
		interact: true,
		plugins: useMemo(() => (showHover ? [recordPlugin, hoverPlugin] : [recordPlugin]), [showHover]),
	});

	const hideAudio = useMemo(() => !isRecording && !audioRecording, [isRecording, audioRecording]);

	useEffect(() => {
		if (isSaving.current && audioRecording) isSaving.current = false;
		if (!isSaving.current && !audioRecording) wavesurfer?.empty();
	}, [isSaving, audioRecording, wavesurfer]);

	const startRecording = useCallback(() => {
		recordPlugin.startRecording();
		setIsRecording(true);
	}, []);

	const stopRecording = useCallback(() => {
		recordPlugin.stopRecording();
		setIsRecording(false);
		isSaving.current = true;
	}, []);

	const onPlayPause = useCallback(() => {
		if (!isRecording) wavesurfer?.playPause();
	}, [wavesurfer]);

	const deleteAudio = useCallback(() => {
		changeHandler(null);
		wavesurfer?.empty();
	}, [wavesurfer, changeHandler]);

	const recordingComplete = useCallback(
		(recording: Blob) => {
			const reader = new window.FileReader();
			reader.onloadend = () => {
				changeHandler({
					name: label,
					type: "audio/webm",
					url: reader.result,
				} as SerializedFile);
			};
			reader.readAsDataURL(recording);
		},
		[changeHandler],
	);

	recordPlugin.on("record-data-available", recordingComplete);

	const PlayPauseButton = useCallback(() => {
		if (!audioRecording && !isSaving.current) return null;
		return (
			<Button onClick={onPlayPause} size={ButtonSize.large} disabled={isSaving.current}>
				<FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
			</Button>
		);
	}, [audioRecording, isSaving, isPlaying, onPlayPause]);

	const DeleteButton = useCallback(() => {
		if (!audioRecording && !isSaving.current) return null;
		return (
			<Button
				onClick={deleteAudio}
				size={ButtonSize.large}
				types={[ButtonTypes.Danger]}
				disabled={isSaving.current}
			>
				<FontAwesomeIcon icon={faTrash} />
			</Button>
		);
	}, [audioRecording, isSaving, deleteAudio]);

	const RecordButtons = useCallback(() => {
		if (audioRecording || isSaving.current) return null;

		if (isRecording) {
			return (
				<Button onClick={stopRecording} size={ButtonSize.large} types={[ButtonTypes.Danger]}>
					<FontAwesomeIcon icon={faStop} />
				</Button>
			);
		}
		return (
			<Button onClick={startRecording} size={ButtonSize.large} types={[ButtonTypes.Primary]}>
				<FontAwesomeIcon icon={faMicrophone} />
			</Button>
		);
	}, [audioRecording, isSaving, isRecording, stopRecording, startRecording]);

	return (
		<>
			<InputComponent
				{...props}
				className={style.container}
				hideInputGroupClass
				hideFormGroupClass
				hideAddon
				hideLabel
			>
				<div className={style.audioContainer}>
					<RecordButtons />
					<PlayPauseButton />
					<div className={style.recording}>
						<div className={styles(style.placeholder, !hideAudio && style.hidden)}>
							{isSaving.current ? "Loading..." : "Press Record to start recording..."}
						</div>
						<div
							onClick={onPlayPause}
							className={styles(hideAudio && style.hidden, isRecording && style.disabled)}
							ref={containerRef}
						/>
					</div>
					<DeleteButton />
				</div>
			</InputComponent>
		</>
	);
}
